/**
 * Portal Mode Hooks
 *
 * Centralizes portal-mode detection logic that is currently scattered
 * across many components. There are two layers of portal mode in Helix:
 *
 * 1. **User Company Type** (from AuthContext):
 *    The user's company is either 'supplier', 'customer', or 'helix'.
 *    Accessed via `user.company_type` (lowercase) or `user.platform` (uppercase).
 *    This is the fundamental role of the user and does not change during a session.
 *
 * 2. **View Context Portal Mode** (from ViewContext):
 *    For companies with `portal_mode === 'both'`, the user can toggle between
 *    supplier and customer views. This is the active viewing perspective.
 *
 * These hooks unify both concepts:
 * - `usePortalMode()` returns the full portal mode state
 * - `useIsCustomer()` returns true if the user is acting as a customer
 * - `useIsSupplier()` returns true if the user is acting as a supplier
 *
 * The hooks use the ViewContext portal mode when dual mode is supported,
 * falling back to the user's company type otherwise.
 *
 * Files that would benefit from adopting these hooks:
 * - frontend/src/pages/Purchasing.tsx
 * - frontend/src/pages/StockLevelsPage.tsx
 * - frontend/src/pages/Concessions.tsx
 * - frontend/src/pages/CustomerQualifications.tsx
 * - frontend/src/pages/QualificationDetail.tsx
 * - frontend/src/pages/ConcessionDetail.tsx
 * - frontend/src/pages/NCREdit.tsx
 * - frontend/src/pages/CustomerDetail.tsx
 * - frontend/src/pages/onboarding/OnboardingDashboard.tsx
 * - frontend/src/pages/qualification-detail/useQualificationDetail.ts
 * - frontend/src/pages/concession-detail/useConcessionDetail.ts
 * - frontend/src/pages/concessions-list/useConcessions.ts
 * - frontend/src/pages/customer-detail/useCustomerDetail.ts
 * - frontend/src/pages/ncr-edit/useNCREdit.ts
 * - frontend/src/components/purchasing/CustomerQueryTab.tsx
 * - frontend/src/components/purchasing/POLineDetailModal.tsx
 * - frontend/src/components/purchasing/tabs/ASNTab.tsx
 * - frontend/src/components/purchasing/tabs/po-lines/usePOLinesState.ts
 * - frontend/src/components/tour/WelcomeModal.tsx
 * - frontend/src/components/tour/QuickReferenceModal.tsx
 */

import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { useView, type PortalMode } from '../contexts/ViewContext';

/**
 * Determines the effective company type based on user data.
 *
 * The user object carries role information in two fields:
 * - `company_type`: lowercase ('supplier' | 'customer' | 'helix')
 * - `platform`: uppercase ('SUPPLIER' | 'CUSTOMER' | 'HELIX')
 *
 * Both represent the same concept; this normalizes them to lowercase.
 */
function resolveCompanyType(
  companyType?: 'supplier' | 'customer' | 'helix' | 'logistics',
  platform?: 'SUPPLIER' | 'CUSTOMER' | 'HELIX'
): 'supplier' | 'customer' | 'helix' | 'logistics' | undefined {
  if (companyType) return companyType;
  if (platform) return platform.toLowerCase() as 'supplier' | 'customer' | 'helix';
  return undefined;
}

export interface PortalModeState {
  /** The active portal mode from ViewContext ('supplier' | 'customer') */
  portalMode: PortalMode;

  /** Whether the user's company supports both supplier and customer views */
  supportsDualMode: boolean;

  /** The user's underlying company type (does not change with view switching) */
  companyType: 'supplier' | 'customer' | 'helix' | 'logistics' | undefined;

  /** True if the user is currently viewing as a customer (via role or active view) */
  isCustomer: boolean;

  /** True if the user is currently viewing as a supplier (includes helix admins) */
  isSupplier: boolean;

  /** True if the user belongs to the Helix admin platform */
  isHelix: boolean;

  /** Supplier-portal tenant user acting as the platform supplier (e.g. Poeton admin) */
  isSupplierPortalSupplier: boolean;
}

/**
 * Returns the complete portal mode state, combining the user's company type
 * with the active view context portal mode.
 *
 * For dual-mode companies, `isCustomer` and `isSupplier` reflect the
 * currently selected view rather than the underlying company type.
 *
 * Usage:
 * ```ts
 * const { isCustomer, isSupplier, portalMode } = usePortalMode();
 * ```
 */
export function usePortalMode(): PortalModeState {
  const { user } = useAuth();
  const { portalMode, supportsDualMode, isPlatformSupplier } = useView();
  const { isSupplierPortalMode } = useMenuSettings();

  return useMemo(() => {
    const companyType = resolveCompanyType(user?.company_type, user?.platform);
    const isAdminUser = !!user?.is_staff || !!user?.is_superuser;
    const isSupplierPortalSupplier = isSupplierPortalMode && (isPlatformSupplier || isAdminUser);

    // For dual-mode companies, defer to the active view context portal mode.
    // For single-mode companies, use the company type directly.
    let effectiveMode: PortalMode = supportsDualMode
      ? portalMode
      : (companyType === 'customer' ? 'customer' : 'supplier');

    // Supplier-portal tenants stored as customer in the DB are forced to the
    // supplier-side nav. Dual-mode supplier companies (Poeton sites) may still
    // toggle to customer view to see inbound POs from sibling sites.
    if (isSupplierPortalSupplier && effectiveMode === 'customer' && !supportsDualMode) {
      effectiveMode = 'supplier';
    }

    const isCustomer = effectiveMode === 'customer';
    const isSupplier = effectiveMode === 'supplier';
    const isHelix = companyType === 'helix';

    return {
      portalMode: effectiveMode,
      supportsDualMode,
      companyType,
      isCustomer,
      isSupplier,
      isHelix,
      isSupplierPortalSupplier,
    };
  }, [
    user?.company_type,
    user?.platform,
    user?.is_staff,
    user?.is_superuser,
    portalMode,
    supportsDualMode,
    isSupplierPortalMode,
    isPlatformSupplier,
  ]);
}

/**
 * Returns true if the user is currently acting as a customer.
 *
 * This checks the view context portal mode for dual-mode companies,
 * and falls back to the user's company type for single-mode companies.
 *
 * Replaces scattered patterns like:
 * ```ts
 * const isCustomer = user?.company_type === 'customer';
 * const isCustomer = user?.platform === 'CUSTOMER';
 * ```
 */
export function useIsCustomer(): boolean {
  const { isCustomer } = usePortalMode();
  return isCustomer;
}

/**
 * Returns true if the user is currently acting as a supplier.
 *
 * This includes Helix admin users, matching the existing convention where
 * `company_type === 'helix'` is treated as a supplier-like role.
 *
 * Replaces scattered patterns like:
 * ```ts
 * const isSupplier = user?.company_type === 'supplier' || user?.company_type === 'helix';
 * const isSupplier = user?.platform === 'SUPPLIER' || user?.platform === 'HELIX';
 * ```
 */
export function useIsSupplier(): boolean {
  const { isSupplier, isHelix } = usePortalMode();
  return isSupplier || isHelix;
}
